import React, { useState, useEffect, useCallback } from 'react';
import {
  XMarkIcon,
  PaperClipIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { residentsAPI } from '../../services/api';

const DisputeFormModal = ({ initialData, onSubmit, onClose }) => {
  // Form state
  const [formData, setFormData] = useState({
    complainant_name: '',
    complainant_type: 'resident',
    complainant_address: '',
    complainant_contact: '',
    complainant_email: '',
    complainant_id: null,
    respondent_name: '',
    respondent_type: 'resident',
    respondent_address: '',
    respondent_contact: '',
    respondent_email: '',
    respondent_id: null,
    incident_date: '',
    incident_time: '',
    incident_location: '',
    complaint_details: '',
    incident_type: '',
    evidence_files: [],
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [searchResults, setSearchResults] = useState({
    complainant: [],
    respondent: [],
  });
  const [searchLoading, setSearchLoading] = useState({
    complainant: false,
    respondent: false,
  });
  const [showSearchResults, setShowSearchResults] = useState({
    complainant: false,
    respondent: false,
  });
  const [searchQuery, setSearchQuery] = useState({
    complainant: '',
    respondent: '',
  });

  // Initialize form with initial data if editing
  useEffect(() => {
    if (initialData) {
      // Set form data
      setFormData({
        ...formData,
        ...initialData,
        incident_date: initialData.incident_date
          ? new Date(initialData.incident_date).toISOString().split('T')[0]
          : '',
        incident_time: initialData.incident_time || '',
      });

      // Set existing evidence files if available
      if (
        initialData.evidence_files &&
        Array.isArray(initialData.evidence_files)
      ) {
        setExistingFiles(initialData.evidence_files);
      }
    }
  }, [initialData]);

  // Search for residents
  const searchResidents = useCallback(async (query, type) => {
    if (query.length < 3) return;

    setSearchLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const response = await residentsAPI.getAll({ search: query, perPage: 5 });
      setSearchResults((prev) => ({
        ...prev,
        [type]: response.data.data || [],
      }));
      setShowSearchResults((prev) => ({ ...prev, [type]: true }));
    } catch (error) {
      console.error(`Error searching for ${type}:`, error);
    } finally {
      setSearchLoading((prev) => ({ ...prev, [type]: false }));
    }
  }, []);

  // Handle search input change
  const handleSearchChange = (e, type) => {
    const query = e.target.value;
    setSearchQuery((prev) => ({ ...prev, [type]: query }));

    if (query.length >= 3) {
      searchResidents(query, type);
    } else {
      setShowSearchResults((prev) => ({ ...prev, [type]: false }));
    }
  };

  // Select a resident from search results
  const selectResident = (resident, type) => {
    const fieldPrefix = type === 'complainant' ? 'complainant' : 'respondent';

    // Format address from object to string if it's an object
    let formattedAddress = '';
    if (resident.address) {
      if (typeof resident.address === 'object') {
        const addr = resident.address;
        const parts = [
          addr.house_number,
          addr.street,
          addr.purok_zone,
          addr.barangay,
          addr.municipality,
          addr.province,
          addr.zip_code,
        ].filter(Boolean); // Remove empty/undefined values
        formattedAddress = parts.join(', ');
      } else {
        formattedAddress = resident.address;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [`${fieldPrefix}_name`]: `${resident.first_name} ${resident.last_name}`,
      [`${fieldPrefix}_address`]: formattedAddress,
      [`${fieldPrefix}_contact`]: resident.contact_number || '',
      [`${fieldPrefix}_id`]: resident.id,
      [`${fieldPrefix}_type`]: 'resident',
    }));

    setSearchQuery((prev) => ({
      ...prev,
      [type]: `${resident.first_name} ${resident.last_name}`,
    }));
    setShowSearchResults((prev) => ({ ...prev, [type]: false }));
  };

  // Mark as non-resident
  const markAsNonResident = (type) => {
    const fieldPrefix = type === 'complainant' ? 'complainant' : 'respondent';

    setFormData((prev) => ({
      ...prev,
      [`${fieldPrefix}_type`]: 'non-resident',
      [`${fieldPrefix}_id`]: null,
    }));

    setShowSearchResults((prev) => ({ ...prev, [type]: false }));
  };

  // Form input change handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user makes changes
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // File input change handler
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setEvidenceFiles((prev) => [...prev, ...files]);
  };

  // Remove file handler
  const handleRemoveFile = (index) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Validate form
    const newErrors = {};
    if (!formData.complainant_name)
      newErrors.complainant_name = 'Complainant name is required';
    if (!formData.respondent_name)
      newErrors.respondent_name = 'Respondent name is required';
    if (!formData.incident_date)
      newErrors.incident_date = 'Incident date is required';
    if (!formData.incident_location)
      newErrors.incident_location = 'Incident location is required';
    if (!formData.complaint_details)
      newErrors.complaint_details = 'Complaint details are required';
    // Contact numbers should not be the same
    if (
      formData.complainant_contact &&
      formData.respondent_contact &&
      formData.complainant_contact === formData.respondent_contact
    ) {
      newErrors.complainant_contact =
        'Complainant and respondent cannot have the same contact number';
      newErrors.respondent_contact =
        'Complainant and respondent cannot have the same contact number';
    }
    if (
      formData.complainant_type === 'non-resident' &&
      !formData.complainant_email
    ) {
      newErrors.complainant_email =
        'Email is required for non-resident complainant';
    }

    if (
      formData.respondent_type === 'non-resident' &&
      !formData.respondent_email
    ) {
      newErrors.respondent_email =
        'Email is required for non-resident respondent';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    // Prepare form data with files
    const submitData = new FormData();

    // Add all form fields to FormData
    for (const key in formData) {
      if (key !== 'evidence_files') {
        // Convert null values to empty strings to prevent backend errors
        const value = formData[key] === null ? '' : formData[key];
        submitData.append(key, value);
      }
    }

    // Append evidence files with correct naming convention for Laravel
    if (evidenceFiles.length > 0) {
      evidenceFiles.forEach((file, index) => {
        submitData.append(`evidence_files[${index}]`, file);
      });
    }

    try {
      // Use the FormData object with files, not the regular formData object
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({ form: 'Failed to submit form. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Available incident types
  const incidentTypes = [
    'Physical Altercation',
    'Verbal Harassment',
    'Property Damage',
    'Noise Complaint',
    'Boundary Dispute',
    'Unpaid Debt',
    'Animal-related Issue',
    'Nuisance',
    'Threat',
    'Others',
  ];

  return (
    // <div
    //   className="fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 pt-16 pb-6 px-4 overflow-y-auto"
    //   role="dialog"
    //   aria-modal="true"
    //   aria-labelledby="dispute-details-title"
    // >
    <div
      className='fixed inset-0 bg-black bg-opacity-60 flex items-start justify-center z-50 pt-16 pb-6 px-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby='dispute-details-title'
    >
      {/* Style tags */}
      {/* <style>{hideScrollbarStyle}</style> */}
      {/* <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl border border-gray-100"> */}
      <div className='bg-white rounded-lg shadow-2xl w-full max-w-5xl border border-gray-100 flex flex-col'>
        <div className='bg-gradient-to-r from-blue-50 to-white-100 flex justify-between items-center px-6 py-5 border-b border-gray-200 rounded-t-lg'>
          <h2 className='text-xl font-semibold text-gray-800'>
            {initialData ? 'Edit Dispute Record' : 'File New Blotter'}
          </h2>
          <button
            onClick={onClose}
            className='text-gray-500 hover:text-gray-700 focus:outline-none'
          >
            <XMarkIcon className='h-6 w-6' />
          </button>
        </div>

        <div className='p-6 overflow-y-auto max-h-[80vh] hide-scrollbar'>
          <form onSubmit={handleSubmit} className='p-6'>
            {errors.form && (
              <div className='mb-4 p-3 bg-red-100 text-red-700 rounded-lg'>
                {errors.form}
              </div>
            )}

            <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
              {/* Complainant Information */}
              <div className='border border-gray-200 rounded-lg p-4'>
                <h3 className='text-lg font-medium text-gray-700 mb-4'>
                  Complainant Information
                </h3>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Complainant Type
                  </label>
                  <div className='flex space-x-4'>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        name='complainant_type'
                        value='resident'
                        checked={formData.complainant_type === 'resident'}
                        onChange={handleChange}
                        className='form-radio h-6 w-6 text-blue-600'
                      />
                      <span className='ml-2 text-gray-700'>Resident</span>
                    </label>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        name='complainant_type'
                        value='non-resident'
                        checked={formData.complainant_type === 'non-resident'}
                        onChange={handleChange}
                        className='form-radio h-6 w-6 text-blue-600'
                      />
                      <span className='ml-2 text-gray-700'>Non-resident</span>
                    </label>
                  </div>
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Full Name <span className='text-red-500'>*</span>
                  </label>
                  <div className='relative'>
                    <input
                      type='text'
                      name='complainant_search'
                      value={searchQuery.complainant}
                      onChange={(e) => handleSearchChange(e, 'complainant')}
                      className={`w-full border ${
                        errors.complainant_name
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg px-3 py-2 pl-10`}
                      placeholder='Search for resident by name'
                      disabled={formData.complainant_type === 'non-resident'}
                    />
                    <div className='absolute left-3 top-2.5 text-gray-400'>
                      <MagnifyingGlassIcon className='h-5 w-5' />
                    </div>

                    {formData.complainant_type === 'non-resident' && (
                      <input
                        type='text'
                        name='complainant_name'
                        value={formData.complainant_name}
                        onChange={handleChange}
                        className={`mt-2 w-full border ${
                          errors.complainant_name
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } rounded-lg px-3 py-2`}
                        placeholder='Enter non-resident name'
                        required
                      />
                    )}

                    {showSearchResults.complainant &&
                      searchResults.complainant.length > 0 && (
                        <div className='absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg max-h-60 overflow-auto'>
                          {searchResults.complainant.map((resident) => (
                            <div
                              key={resident.id}
                              className='p-2 hover:bg-blue-50 cursor-pointer flex items-center'
                              onClick={() =>
                                selectResident(resident, 'complainant')
                              }
                            >
                              <div className='w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-2'>
                                <UserIcon className='h-5 w-5' />
                              </div>
                              <div>
                                <div className='font-medium'>
                                  {resident.first_name} {resident.last_name}
                                </div>
                                <div className='text-xs text-gray-500'>
                                  {typeof resident.address === 'object'
                                    ? [
                                        resident.address.house_number,
                                        resident.address.street,
                                        resident.address.barangay,
                                      ]
                                        .filter(Boolean)
                                        .join(', ') || 'No address'
                                    : resident.address || 'No address'}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div
                            className='p-2 hover:bg-gray-100 cursor-pointer text-center text-gray-700 border-t'
                            onClick={() => markAsNonResident('complainant')}
                          >
                            Not found? Mark as non-resident
                          </div>
                        </div>
                      )}
                  </div>
                  {errors.complainant_name && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.complainant_name}
                    </p>
                  )}
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Address
                  </label>
                  <textarea
                    name='complainant_address'
                    value={formData.complainant_address}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    placeholder='Complete address'
                    rows={2}
                  />
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Contact Number
                  </label>
                  <input
                    type='text'
                    name='complainant_contact'
                    value={formData.complainant_contact}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    placeholder='Contact number'
                  />
                  {errors.complainant_contact && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.complainant_contact}
                    </p>
                  )}
                </div>
                {/* EMAIL */}
                {formData.complainant_type === 'non-resident' && (
                  <div className='mb-4'>
                    <label className='block text-gray-700 text-sm font-medium mb-1'>
                      Email Address <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='email'
                      name='complainant_email'
                      value={formData.complainant_email}
                      onChange={handleChange}
                      className={`w-full border ${
                        errors.complainant_email
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg px-3 py-2`}
                      placeholder='Enter email address'
                      required={formData.complainant_type === 'non-resident'}
                    />
                    {errors.complainant_email && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.complainant_email}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Respondent Information */}
              <div className='border border-gray-200 rounded-lg p-4'>
                <h3 className='text-lg font-medium text-gray-700 mb-4'>
                  Respondent Information
                </h3>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Respondent Type
                  </label>
                  <div className='flex space-x-4'>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        name='respondent_type'
                        value='resident'
                        checked={formData.respondent_type === 'resident'}
                        onChange={handleChange}
                        className='form-radio h-6 w-6 text-blue-600'
                      />
                      <span className='ml-2 text-gray-700'>Resident</span>
                    </label>
                    <label className='inline-flex items-center'>
                      <input
                        type='radio'
                        name='respondent_type'
                        value='non-resident'
                        checked={formData.respondent_type === 'non-resident'}
                        onChange={handleChange}
                        className='form-radio h-6 w-6 text-blue-600'
                      />
                      <span className='ml-2 text-gray-700'>Non-resident</span>
                    </label>
                  </div>
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Full Name <span className='text-red-500'>*</span>
                  </label>
                  <div className='relative'>
                    <input
                      type='text'
                      name='respondent_search'
                      value={searchQuery.respondent}
                      onChange={(e) => handleSearchChange(e, 'respondent')}
                      className={`w-full border ${
                        errors.respondent_name
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg px-3 py-2 pl-10`}
                      placeholder='Search for resident by name'
                      disabled={formData.respondent_type === 'non-resident'}
                    />
                    <div className='absolute left-3 top-2.5 text-gray-400'>
                      <MagnifyingGlassIcon className='h-5 w-5' />
                    </div>

                    {formData.respondent_type === 'non-resident' && (
                      <input
                        type='text'
                        name='respondent_name'
                        value={formData.respondent_name}
                        onChange={handleChange}
                        className={`mt-2 w-full border ${
                          errors.respondent_name
                            ? 'border-red-500'
                            : 'border-gray-300'
                        } rounded-lg px-3 py-2`}
                        placeholder='Enter non-resident name'
                        required
                      />
                    )}

                    {showSearchResults.respondent &&
                      searchResults.respondent.length > 0 && (
                        <div className='absolute z-10 w-full mt-1 bg-white shadow-lg rounded-lg max-h-60 overflow-auto'>
                          {searchResults.respondent.map((resident) => (
                            <div
                              key={resident.id}
                              className='p-2 hover:bg-blue-50 cursor-pointer flex items-center'
                              onClick={() =>
                                selectResident(resident, 'respondent')
                              }
                            >
                              <div className='w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center mr-2'>
                                <UserIcon className='h-5 w-5' />
                              </div>
                              <div>
                                <div className='font-medium'>
                                  {resident.first_name} {resident.last_name}
                                </div>
                                <div className='text-xs text-gray-500'>
                                  {typeof resident.address === 'object'
                                    ? [
                                        resident.address.house_number,
                                        resident.address.street,
                                        resident.address.barangay,
                                      ]
                                        .filter(Boolean)
                                        .join(', ') || 'No address'
                                    : resident.address || 'No address'}
                                </div>
                              </div>
                            </div>
                          ))}
                          <div
                            className='p-2 hover:bg-gray-100 cursor-pointer text-center text-gray-700 border-t'
                            onClick={() => markAsNonResident('respondent')}
                          >
                            Not found? Mark as non-resident
                          </div>
                        </div>
                      )}
                  </div>
                  {errors.respondent_name && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.respondent_name}
                    </p>
                  )}
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Address
                  </label>
                  <textarea
                    name='respondent_address'
                    value={formData.respondent_address}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    placeholder='Complete address'
                    rows={2}
                  />
                </div>

                <div className='mb-4'>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Contact Number
                  </label>
                  <input
                    type='text'
                    name='respondent_contact'
                    value={formData.respondent_contact}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    placeholder='Contact number'
                  />
                  {errors.respondent_contact && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.respondent_contact}
                    </p>
                  )}
                </div>

                {formData.respondent_type === 'non-resident' && (
                  <div className='mb-4'>
                    <label className='block text-gray-700 text-sm font-medium mb-1'>
                      Email Address <span className='text-red-500'>*</span>
                    </label>
                    <input
                      type='email'
                      name='respondent_email'
                      value={formData.respondent_email}
                      onChange={handleChange}
                      className={`w-full border ${
                        errors.respondent_email
                          ? 'border-red-500'
                          : 'border-gray-300'
                      } rounded-lg px-3 py-2`}
                      placeholder='Enter email address'
                      required={formData.respondent_type === 'non-resident'}
                    />
                    {errors.respondent_email && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.respondent_email}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Incident Details */}
            <div className='mt-6 border border-gray-200 rounded-lg p-4'>
              <h3 className='text-lg font-medium text-gray-700 mb-4'>
                Incident Details
              </h3>

              <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-4'>
                <div>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Incident Date <span className='text-red-500'>*</span>
                  </label>
                  <input
                    type='date'
                    name='incident_date'
                    value={formData.incident_date}
                    onChange={handleChange}
                    className={`w-full border ${
                      errors.incident_date
                        ? 'border-red-500'
                        : 'border-gray-300'
                    } rounded-lg px-3 py-2`}
                    required
                  />
                  {errors.incident_date && (
                    <p className='text-red-500 text-xs mt-1'>
                      {errors.incident_date}
                    </p>
                  )}
                </div>

                <div>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Incident Time
                  </label>
                  <input
                    type='time'
                    name='incident_time'
                    value={formData.incident_time}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                  />
                </div>

                <div>
                  <label className='block text-gray-700 text-sm font-medium mb-1'>
                    Incident Type <span className='text-red-500'>*</span>
                  </label>
                  <select
                    name='incident_type'
                    value={formData.incident_type}
                    onChange={handleChange}
                    className='w-full border border-gray-300 rounded-lg px-3 py-2'
                    required
                  >
                    <option value=''>Select type...</option>
                    {incidentTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-medium mb-1'>
                  Incident Location <span className='text-red-500'>*</span>
                </label>
                <input
                  type='text'
                  name='incident_location'
                  value={formData.incident_location}
                  onChange={handleChange}
                  className={`w-full border ${
                    errors.incident_location
                      ? 'border-red-500'
                      : 'border-gray-300'
                  } rounded-lg px-3 py-2`}
                  placeholder='Where did the incident occur?'
                  required
                />
                {errors.incident_location && (
                  <p className='text-red-500 text-xs mt-1'>
                    {errors.incident_location}
                  </p>
                )}
              </div>

              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-medium mb-1'>
                  Complaint Details <span className='text-red-500'>*</span>
                </label>
                <textarea
                  name='complaint_details'
                  value={formData.complaint_details}
                  onChange={handleChange}
                  className={`w-full border ${
                    errors.complaint_details
                      ? 'border-red-500'
                      : 'border-gray-300'
                  } rounded-lg px-3 py-2`}
                  rows='5'
                  placeholder='Provide detailed description of the complaint...'
                  required
                ></textarea>
                {errors.complaint_details && (
                  <p className='text-red-500 text-xs mt-1'>
                    {errors.complaint_details}
                  </p>
                )}
              </div>
            </div>

            {/* Evidence Attachments */}
            <div className='mt-6 border border-gray-200 rounded-lg p-4'>
              <h3 className='text-lg font-medium text-gray-700 mb-4'>
                Evidence & Attachments
              </h3>

              <div className='mb-4'>
                <label className='block text-gray-700 text-sm font-medium mb-1'>
                  Upload Evidence Files
                </label>
                <div className='mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg'>
                  <div className='space-y-1 text-center'>
                    <PaperClipIcon className='mx-auto h-12 w-12 text-gray-400' />
                    <div className='flex text-sm text-gray-600'>
                      <label
                        htmlFor='file-upload'
                        className='relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none'
                      >
                        <span>Upload files</span>
                        <input
                          id='file-upload'
                          name='file-upload'
                          type='file'
                          multiple
                          onChange={handleFileChange}
                          className='sr-only'
                          accept='.jpg,.jpeg,.png,.pdf,.doc,.docx'
                        />
                      </label>
                      <p className='pl-1'>or drag and drop</p>
                    </div>
                    <p className='text-xs text-gray-500'>
                      PNG, JPG, PDF up to 10MB each
                    </p>
                  </div>
                </div>
              </div>

              {/* Display existing files (if editing) */}
              {existingFiles.length > 0 && (
                <div className='mt-4'>
                  <h4 className='text-sm font-medium text-gray-700 mb-2'>
                    Existing Files:
                  </h4>
                  <ul className='space-y-2'>
                    {existingFiles.map((file, index) => (
                      <li
                        key={`existing-${index}`}
                        className='flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg'
                      >
                        <div className='flex items-center'>
                          <PaperClipIcon className='h-5 w-5 text-gray-400 mr-2' />
                          <span className='text-sm text-gray-700'>
                            {file.filename}
                          </span>
                          <span className='ml-2 text-xs text-gray-500'>
                            ({(file.file_size / 1024).toFixed(2)} KB)
                          </span>
                        </div>
                        {/* No remove button for existing files during edit */}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Display newly selected files */}
              {evidenceFiles.length > 0 && (
                <div className='mt-4'>
                  <h4 className='text-sm font-medium text-gray-700 mb-2'>
                    {existingFiles.length > 0
                      ? 'New Files:'
                      : 'Selected Files:'}
                  </h4>
                  <ul className='space-y-2'>
                    {evidenceFiles.map((file, index) => (
                      <li
                        key={`new-${index}`}
                        className='flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg'
                      >
                        <div className='flex items-center'>
                          <PaperClipIcon className='h-5 w-5 text-gray-400 mr-2' />
                          <span className='text-sm text-gray-700'>
                            {file.name}
                          </span>
                          <span className='ml-2 text-xs text-gray-500'>
                            ({(file.size / 1024).toFixed(2)} KB)
                          </span>
                        </div>
                        <button
                          type='button'
                          onClick={() => handleRemoveFile(index)}
                          className='text-red-500 hover:text-red-700'
                        >
                          <XMarkIcon className='h-5 w-5' />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Form Controls */}
            {/* <div className="mt-6 flex justify-end space-x-3"> */}
            <div className='px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 flex-shrink-0'>
              <button
                type='button'
                onClick={onClose}
                className='px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type='submit'
                className='px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 hover:scale-105'
                disabled={loading}
              >
                {loading
                  ? 'Saving...'
                  : initialData
                  ? 'Update Record'
                  : 'Submit Blotter'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// const hideScrollbarStyle = `
//   .hide-scrollbar {
//     scrollbar-width: none; /* Firefox */
//     -ms-overflow-style: none; /* IE and Edge */
//   }
//   .hide-scrollbar::-webkit-scrollbar {
//     display: none; /* Chrome, Safari, Opera*/
//   }
// `;

export default React.memo(DisputeFormModal);
